import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { supabase } from "../supabaseClient";
import { useSidebar } from "../contexts/SidebarContext";
import { useUser } from "../contexts/UserContext";
import NotificationBell from "./NotificationBell";
import {
  HomeIcon,
  BriefcaseIcon,
  ClockIcon,
  UsersIcon,
  DocumentTextIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  ChevronDownIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  CalendarDaysIcon,
  DocumentDuplicateIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  CalculatorIcon,
} from "@heroicons/react/24/outline";

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [documentsMenuOpen, setDocumentsMenuOpen] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const dropdownButtonRef = useRef(null);
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const { userInfo, loading } = useUser(); // Use context instead of local state

  // Auto-open dropdown if user is on a submenu page
  useEffect(() => {
    const documentsPaths = ['/leave-request', '/leave-permit-form', '/job-application', '/resignation-letter'];
    if (documentsPaths.includes(location.pathname)) {
      setDocumentsMenuOpen(true);
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const menuItems = [
    {
      name: "Dashboard",
      path: "/dashboard",
      icon: HomeIcon,
    },
    {
      name: "Absensi",
      path: "/attendance",
      icon: ClockIcon,
    },
    {
      name: "Timesheet Report",
      path: "/timesheet",
      icon: ClipboardDocumentCheckIcon,
    },
    {
      name: "Task Management",
      path: "/tasks",
      icon: ClipboardDocumentCheckIcon,
    },
    {
      name: "Dokumen & Surat",
      icon: DocumentDuplicateIcon,
      hasDropdown: true,
      submenu: [
        {
          name: "Cuti & Izin Sakit",
          path: "/leave-request",
          icon: CalendarDaysIcon,
        },
        // {
        //   name: "Form Permohonan Izin",
        //   path: "/leave-permit-form",
        //   icon: ClipboardDocumentCheckIcon,
        // },
        {
          name: "Surat Lamaran Kerja",
          path: "/job-application",
          icon: BriefcaseIcon,
        },
        {
          name: "Surat Resign",
          path: "/resignation-letter",
          icon: DocumentTextIcon,
        },
      ],
    },
    {
      name: "CV Preview",
      path: "/cv-preview",
      icon: DocumentTextIcon,
    },
    ...(userInfo?.role === 'admin' ? [{
      name: "Manage Users",
      path: "/admin/users",
      icon: UsersIcon,
    }] : []),
    // Apps Section Divider
    { divider: true, label: "APPS" },
    {
      name: "Split Bill",
      path: "/split-bill",
      icon: CalculatorIcon,
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Navbar - Fixed */}
      <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50 h-16">
        <div className="h-full px-4 flex items-center justify-between">
          {/* Left: Brand + Hamburger */}
          <div className="flex items-center gap-4">
            {/* Mobile Hamburger */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-blue-50 to-blue-50 border border-blue-200 hover:border-blue-400 hover:from-blue-100 hover:to-blue-100 text-blue-600 hover:text-blue-700 transition-all duration-200 shadow-sm hover:shadow-md"
              title="Toggle Menu"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>

            {/* Desktop Toggle Sidebar */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-blue-50 to-blue-50 border border-blue-200 hover:border-blue-400 hover:from-blue-100 hover:to-blue-100 text-blue-600 hover:text-blue-700 transition-all duration-200 shadow-sm hover:shadow-md group"
              title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {isCollapsed ? (
                <ChevronDoubleRightIcon className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              ) : (
                <ChevronDoubleLeftIcon className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
              )}
            </button>

            {/* Brand */}
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img src="/assets/logos/RBApps-Logo-Elegant.svg" alt="RBApps" className="h-14" />
            </Link>
          </div>

          {/* Right: Notification Bell & Profile Menu */}
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <NotificationBell />
            
            {/* Profile Menu */}
            {loading ? (
              // Loading Skeleton
              <div className="flex items-center gap-3 px-3 py-2">
                <div className="w-9 h-9 rounded-full bg-gray-300 animate-pulse"></div>
                <div className="hidden md:block">
                  <div className="w-24 h-3 bg-gray-300 rounded animate-pulse mb-1"></div>
                  <div className="w-16 h-2 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            ) : userInfo ? (
            <div className="relative">
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {/* Profile Picture */}
                <div className="relative flex-shrink-0">
                  {userInfo.photo ? (
                    <img
                      src={userInfo.photo}
                      alt={userInfo.full_name}
                      className="w-9 h-9 rounded-full object-cover border-2 border-blue-500"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xs border-2 border-blue-500">
                      {getInitials(userInfo.full_name)}
                    </div>
                  )}
                  <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white"></div>
                </div>

                {/* User Info - Hidden on small screens */}
                <div className="text-left hidden md:block">
                  <p className="text-sm font-semibold text-gray-800 leading-tight max-w-[150px] truncate">
                    {userInfo.full_name}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {userInfo.role || 'user'}
                  </p>
                </div>

                <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform hidden md:block ${profileMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {profileMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setProfileMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                    {/* Profile Header */}
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="text-sm font-semibold text-gray-800">{userInfo.full_name}</p>
                      <p className="text-xs text-gray-500 truncate">{userInfo.email}</p>
                      {userInfo.role && (
                        <span className="inline-block mt-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          {userInfo.role}
                        </span>
                      )}
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={() => {
                          navigate('/profile')
                          setProfileMenuOpen(false)
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <UserCircleIcon className="w-4 h-4" />
                        View Profile
                      </button>
                      <button
                        onClick={() => {
                          navigate('/edit-profile')
                          setProfileMenuOpen(false)
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        Edit Profile
                      </button>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-gray-200 pt-1">
                      <button
                        onClick={() => {
                          handleLogout()
                          setProfileMenuOpen(false)
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <ArrowLeftOnRectangleIcon className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : null}
          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30 mt-16"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar - Navigation */}
      <aside
        className={`fixed top-16 left-0 h-[calc(100vh-4rem)] bg-white shadow-lg z-[100] transform transition-all duration-300 ease-in-out overflow-visible ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 ${isCollapsed ? 'lg:w-20' : 'w-64'}`}
      >
        <div className="flex flex-col h-full overflow-visible">
          {/* User Info Section - Mobile Only */}
          {userInfo && (
            <div className="lg:hidden px-4 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
              <div className="flex items-center gap-3">
                <div className="relative flex-shrink-0">
                  {userInfo.photo ? (
                    <img
                      src={userInfo.photo}
                      alt={userInfo.full_name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-blue-500"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm border-2 border-blue-500">
                      {getInitials(userInfo.full_name)}
                    </div>
                  )}
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{userInfo.full_name}</p>
                  <p className="text-xs text-gray-500 truncate">{userInfo.email}</p>
                  {userInfo.role && (
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded capitalize">
                      {userInfo.role}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Menu Items */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto overflow-x-visible">
            {menuItems.map((item, index) => {
              // Render Divider
              if (item.divider) {
                return (
                  <div key={`divider-${index}`} className="pt-4 pb-2">
                    <div className="flex items-center gap-2">
                      <div className={`h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent flex-1 ${isCollapsed ? 'lg:hidden' : ''}`}></div>
                      <span className={`text-xs font-bold text-gray-400 tracking-wider ${isCollapsed ? 'lg:hidden' : ''}`}>
                        {item.label}
                      </span>
                      <div className={`h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent flex-1 ${isCollapsed ? 'lg:hidden' : ''}`}></div>
                    </div>
                    {/* Collapsed mode divider */}
                    {isCollapsed && (
                      <div className="hidden lg:block">
                        <div className="h-px bg-gray-300 my-2"></div>
                      </div>
                    )}
                  </div>
                );
              }
              
              const Icon = item.icon;
              
              // Jika item memiliki dropdown
              if (item.hasDropdown && item.submenu) {
                const isAnySubmenuActive = item.submenu.some(sub => isActive(sub.path));
                
                const handleMouseEnter = () => {
                  if (isCollapsed && dropdownButtonRef.current) {
                    if (hoverTimeout) clearTimeout(hoverTimeout);
                    // Update position setiap kali hover
                    const rect = dropdownButtonRef.current.getBoundingClientRect();
                    setPopupPosition({
                      top: rect.top,
                      left: rect.right + 8
                    });
                    setDocumentsMenuOpen(true);
                  }
                };
                
                const handleClick = () => {
                  if (isCollapsed && dropdownButtonRef.current) {
                    // Update position saat diklik juga
                    const rect = dropdownButtonRef.current.getBoundingClientRect();
                    setPopupPosition({
                      top: rect.top,
                      left: rect.right + 8
                    });
                  }
                  setDocumentsMenuOpen(!documentsMenuOpen);
                };

                const handleMouseLeave = () => {
                  if (isCollapsed) {
                    const timeout = setTimeout(() => {
                      setDocumentsMenuOpen(false);
                    }, 200); // Delay 200ms sebelum menutup
                    setHoverTimeout(timeout);
                  }
                };
                
                return (
                  <div 
                    key={item.name} 
                    className="relative"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {/* Parent Menu Button */}
                    <button
                      ref={dropdownButtonRef}
                      onClick={handleClick}
                      title={isCollapsed ? item.name : ''}
                      className={`flex items-center justify-between w-full gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                        isAnySubmenuActive
                          ? "bg-blue-600 text-white shadow-md"
                          : "text-gray-700 hover:bg-gray-100"
                      } ${isCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span className={`font-medium ${isCollapsed ? 'lg:hidden' : ''}`}>
                          {item.name}
                        </span>
                      </div>
                      {!isCollapsed && (
                        documentsMenuOpen ? 
                          <ChevronUpIcon className="w-4 h-4 flex-shrink-0" /> :
                          <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
                      )}
                    </button>
                    
                    {/* Submenu Items - Normal (Not Collapsed) */}
                    {documentsMenuOpen && !isCollapsed && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setIsOpen(false)}
                              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                                isActive(subItem.path)
                                  ? "bg-blue-100 text-blue-700 font-semibold"
                                  : "text-gray-600 hover:bg-gray-50"
                              }`}
                            >
                              <SubIcon className="w-4 h-4 flex-shrink-0" />
                              <span>{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}

                    {/* Submenu Popup - Collapsed Mode using Portal */}
                    {documentsMenuOpen && isCollapsed && popupPosition.top > 0 && createPortal(
                      <div 
                        className="fixed w-56 bg-white rounded-lg shadow-2xl border border-gray-200 py-2 z-[9999]"
                        style={{
                          top: `${popupPosition.top}px`,
                          left: `${popupPosition.left}px`
                        }}
                        onMouseEnter={() => {
                          if (hoverTimeout) clearTimeout(hoverTimeout);
                        }}
                        onMouseLeave={() => {
                          const timeout = setTimeout(() => {
                            setDocumentsMenuOpen(false);
                          }, 200);
                          setHoverTimeout(timeout);
                        }}
                      >
                        <div className="px-3 py-2 border-b border-gray-200">
                          <p className="text-xs font-semibold text-gray-500 uppercase">{item.name}</p>
                        </div>
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => {
                                if (hoverTimeout) clearTimeout(hoverTimeout);
                                setIsOpen(false);
                                setDocumentsMenuOpen(false);
                              }}
                              className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                isActive(subItem.path)
                                  ? "bg-blue-50 text-blue-700 font-semibold"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <SubIcon className="w-4 h-4 flex-shrink-0" />
                              <span>{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </div>,
                      document.body
                    )}
                  </div>
                );
              }
              
              // Menu biasa tanpa dropdown
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  title={isCollapsed ? item.name : ''}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-gray-700 hover:bg-gray-100"
                  } ${isCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className={`font-medium ${isCollapsed ? 'lg:hidden' : ''}`}>
                    {item.name}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="px-4 py-4 border-t border-gray-200 bg-gradient-to-r from-red-50 to-orange-50">
            <button
              onClick={() => {
                handleLogout();
                setIsOpen(false);
              }}
              title={isCollapsed ? 'Logout' : ''}
              className={`flex items-center gap-3 w-full px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg font-semibold hover:scale-[1.02] ${
                isCollapsed ? 'lg:justify-center lg:px-2' : ''
              }`}
            >
              <ArrowLeftOnRectangleIcon className="w-5 h-5 flex-shrink-0" />
              <span className={`${isCollapsed ? 'lg:hidden' : ''}`}>
                Logout
              </span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
